import { DOCUMENT } from '@angular/common';
import { APP_ID, DestroyRef, Inject, NgModule, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { SwRegistrationOptions } from '@angular/service-worker';
import { BrandingService } from '@next/core-lib/branding';
import { AgrBrowserModule } from '@next/core-lib/browser';

import { AgrEnvironment, EnvironmentInjector } from '@next/core-lib/environment';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { LocaleService } from '@next/core-lib/i18n';
import { AgrNotificationBrowserModule } from '@next/core-lib/notification-browser';
import { PWA_INSTALL_PROMPT_MODE } from '@next/core-lib/pwa';

import { environment } from 'src/environments/environment';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

import { AppComponent } from './app.component';
import { AppModule } from './app.module';
import { applyPwaBranding, getPwaAppName } from './shared/pwa/pwa-branding';
import { PwaBrandingService } from './shared/pwa/pwa-branding.service';
import { pwaRegistrationOptions } from './shared/pwa/pwa-worker';
import { loadRuntimeEnvironment } from './shared/pwa/runtime-environment';

const loadedConfig = signal(environment);

export const setupRuntimeConfig = (async () => {
  const resolvedEnvironment = await loadRuntimeEnvironment(
    environment,
    window.location.hostname,
    globalThis.CONFIG_BUILD_ID,
  );
  loadedConfig.set(resolvedEnvironment);
  document.title = getPwaAppName(resolvedEnvironment.TENANT, resolvedEnvironment.DEFAULT_LOCALE);
  applyPwaBranding(document, resolvedEnvironment.TENANT, resolvedEnvironment.DEFAULT_LOCALE);
})();

@NgModule({
  imports: [AppModule, AgrBrowserModule, AgrNotificationBrowserModule],
  providers: [
    { provide: SwRegistrationOptions, useFactory: pwaRegistrationOptions },
    { provide: BrandingService, useClass: PwaBrandingService },
    { provide: Title, useExisting: BrandingService },
    { provide: PWA_INSTALL_PROMPT_MODE, useValue: 'native' },
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => loadedConfig(),
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppBrowserModule {
  constructor(
    @Inject(DOCUMENT) document: Document,
    @Inject(EnvironmentInjector) env: AgrEnvironment,
    locale: LocaleService,
    destroyRef: DestroyRef,
  ) {
    locale.locale$.pipe(takeUntilDestroyed(destroyRef)).subscribe((language) => {
      applyPwaBranding(document, env.TENANT, language);
    });
  }
}
